import React, { useEffect, useState, useCallback } from "react";
import "@/App.css";
import { COMPANY, RECIPIENTS, NAV } from "./proposal/data";
import SectionsOne from "./proposal/sections1";
import SectionsTwo from "./proposal/sections2";
import SectionsThree from "./proposal/sections3";
import { Download, FileText, ArrowUpRight } from "lucide-react";

function Cover() {
  return (
    <div className="cover" data-testid="cover">
      <div className="grain" />
      <div className="cover-inner">
        <div className="seal">Confidential Federal Partnership Proposal · {COMPANY.date}</div>
        <img className="logo" src={COMPANY.logo} alt="Generational Health" />
        <h1>The National Behavioral Health <span className="accent">Intelligence Platform</span></h1>
        <p className="sub">{COMPANY.docSubtitle}</p>
        <div className="recipients">
          {RECIPIENTS.map((r, i) => <span key={i}>{r}</span>)}
        </div>
      </div>
      <div className="cover-inner" style={{ paddingTop: 0 }}>
        <div className="cover-foot">
          <div>Prepared by <b>{COMPANY.founder}</b>, {COMPANY.founderTitle} · <b>{COMPANY.name}</b></div>
          <div>{COMPANY.city} · <b>{COMPANY.website}</b> · {COMPANY.docTitle} {COMPANY.version}</div>
        </div>
      </div>
    </div>
  );
}

function Footer() {
  return (
    <footer className="footer" data-testid="footer">
      <div className="footer-inner">
        <div style={{ maxWidth: 360 }}>
          <img src={COMPANY.logo} alt="Generational Health" />
          <p>{COMPANY.tagline}<br />Charlotte, North Carolina 28203</p>
        </div>
        <div>
          <h5>Contact</h5>
          <p>{COMPANY.founder}<br />{COMPANY.founderTitle}<br /><a className="f-link" href={`https://${COMPANY.website}`} target="_blank" rel="noreferrer">{COMPANY.website} <ArrowUpRight size={12} style={{ display: "inline" }} /></a></p>
        </div>
        <div style={{ maxWidth: 320 }}>
          <h5>About this document</h5>
          <p>{COMPANY.docTitle} {COMPANY.version} · {COMPANY.date}. National statistics cited from public federal sources; ROI and outcome projections are illustrative planning estimates as noted in the appendix.</p>
        </div>
      </div>
    </footer>
  );
}

export default function App() {
  const [active, setActive] = useState(NAV[0].id);

  const handlePrint = useCallback(() => window.print(), []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); });
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    );
    NAV.forEach((s) => { const el = document.getElementById(s.id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);

  return (
    <div className="app-shell">
      <aside className="toc-rail no-print" data-testid="toc-rail">
        <div className="toc-brand">
          <img src={COMPANY.logo} alt="Generational Health" />
          <div className="tag">{COMPANY.tagline}</div>
        </div>
        <nav className="toc" data-testid="toc-nav">
          <div className="toc-head">Table of Contents</div>
          {NAV.map((s) => (
            <a key={s.id} href={`#${s.id}`} className={active === s.id ? "active" : ""}
              data-testid={`toc-link-${s.id}`}>
              <span className="n">{String(s.n).padStart(2, "0")}</span>{s.title}
            </a>
          ))}
        </nav>
      </aside>

      <div className="main-col">
        <div className="topbar no-print" data-testid="topbar">
          <div className="doc-meta"><b>{COMPANY.name}</b> &nbsp;·&nbsp; {COMPANY.docTitle} &nbsp;·&nbsp; {COMPANY.version}</div>
          <div style={{ display: "flex", gap: 10 }}>
            <a className="btn" href={`https://${COMPANY.website}`} target="_blank" rel="noreferrer" data-testid="website-btn">
              <FileText size={15} /> {COMPANY.website}
            </a>
            <button className="btn btn-teal" onClick={handlePrint} data-testid="download-pdf-btn">
              <Download size={15} /> Download PDF
            </button>
          </div>
        </div>

        <Cover />

        <main className="doc" data-testid="proposal-document">
          <SectionsOne />
          <SectionsTwo />
          <SectionsThree />
        </main>

        <Footer />
      </div>
    </div>
  );
}
